import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { areShippingAddressesEqual, normalizeShippingAddress } from '../../util/shipping';

import Form from '../Form/Form';
import { H3 } from '../Heading/Heading';
import { PrimaryButton } from '../Button/Button';
import ShippingAddressFields from './ShippingAddressFields';

import css from './ShippingAddressForm.module.css';

/**
 * Shared shipping address form for seller origin and buyer checkout destination.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const ShippingAddressFormComponent = props => {
  const [submittedValues, setSubmittedValues] = React.useState(null);
  const {
    rootClassName,
    className,
    formId = 'ShippingAddressForm',
    initialValues,
    onSubmit,
    inProgress = false,
    saveError = null,
    notice = null,
    submitLabel,
    showHeading = true,
    headingId = 'ShippingAddressForm.title',
    submitDisabled = false,
    validate,
    allowedCountryCodes,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const countPresentValues = values =>
    Object.values(normalizeShippingAddress(values) || {}).filter(value => !!`${value || ''}`.trim())
      .length;

  const getFormValuesFromDom = formElement => {
    if (!formElement) {
      return null;
    }

    return Object.fromEntries(new FormData(formElement).entries());
  };

  return (
    <FinalForm
      initialValues={normalizeShippingAddress(initialValues) || {}}
      onSubmit={(values, form) => {
        const normalizedValues = normalizeShippingAddress(values);
        return Promise.resolve(onSubmit(normalizedValues))
          .then(response => {
            const submittedAddress =
              normalizeShippingAddress(response?.normalizedAddress) || normalizedValues;
            Object.entries(submittedAddress).forEach(([key, value]) => {
              form.change(key, value);
            });
            setSubmittedValues(submittedAddress);
            return response;
          })
          .catch(() => {
            // Keep current form values for retry. Parent surfaces saveError.
          });
      }}
      validate={validate}
      render={({ handleSubmit, form, pristine, values }) => {
        const currentValues = normalizeShippingAddress(values);
        const submitReady =
          !!submittedValues && areShippingAddressesEqual(currentValues, submittedValues);

        return (
          <Form
            className={classes}
            onSubmit={event => {
              const domValues = getFormValuesFromDom(event?.currentTarget);
              const currentValues = form.getState().values;
              const shouldSyncDomValues =
                countPresentValues(domValues) > countPresentValues(currentValues);

              if (shouldSyncDomValues) {
                Object.entries(domValues || {}).forEach(([key, value]) => {
                  form.change(key, value);
                });

                event?.preventDefault();
                return form.submit();
              }

              return handleSubmit(event);
            }}
          >
            {showHeading ? (
              <H3 as="h2" className={css.title}>
                <FormattedMessage id={headingId} />
              </H3>
            ) : null}

            {saveError ? <p className={css.error}>{saveError}</p> : null}
            {notice ? <p className={css.notice}>{notice}</p> : null}

            <ShippingAddressFields
              allowedCountryCodes={allowedCountryCodes}
              form={form}
              formId={formId}
            />

            <div className={css.actions}>
              <PrimaryButton
                type="submit"
                inProgress={inProgress}
                disabled={submitDisabled || pristine}
                ready={submitReady}
                className={css.submitButton}
              >
                <svg
                  style={{ fill: 'transparent' }}
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.79833 0.625H3.54167C2.76812 0.625 2.02625 0.932291 1.47927 1.47927C0.932291 2.02625 0.625 2.76812 0.625 3.54167V11.7308C0.625 12.4321 0.903581 13.1047 1.39946 13.6005C1.89534 14.0964 2.56789 14.375 3.26917 14.375M3.79833 0.625V2.66C3.79833 3.10203 3.97393 3.52595 4.28649 3.83851C4.59905 4.15107 5.02297 4.32667 5.465 4.32667H7.42C7.86203 4.32667 8.28595 4.15107 8.59851 3.83851C8.91107 3.52595 9.08667 3.10203 9.08667 2.66V0.625M3.79833 0.625H9.08667M3.26917 14.375V10C3.26917 9.33696 3.53256 8.70107 4.0014 8.23223C4.47024 7.76339 5.10613 7.5 5.76917 7.5H9.23083C9.89387 7.5 10.5298 7.76339 10.9986 8.23223C11.4674 8.70107 11.7308 9.33696 11.7308 10V14.375M3.26917 14.375H11.7308M9.08667 0.625H9.10833C9.43675 0.624961 9.76196 0.689631 10.0654 0.815316C10.3688 0.941001 10.6445 1.12524 10.8767 1.3575L13.5208 4.00167C13.7917 4.27253 14.0065 4.59408 14.1531 4.94796C14.2996 5.30185 14.375 5.68113 14.375 6.06417V11.7308C14.375 12.4321 14.0964 13.1047 13.6005 13.6005C13.1047 14.0964 12.4321 14.375 11.7308 14.375"
                    stroke="white"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {submitLabel || <FormattedMessage id="ShippingAddressForm.submit" />}
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default ShippingAddressFormComponent;
